import React from "react";
import PhoneActions from "./PhoneActions";

const BREAKER_PRODUCT_LABELS = {
  acb: "ACB (Air circuit breaker)",
  mccb: "MCCB (Molded case circuit breaker)",
  vcb: "VCB (Vacuum circuit breaker)"
};

const getBreakerDisplayName = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const lowerValue = normalized.toLowerCase();
  if (BREAKER_PRODUCT_LABELS[lowerValue]) {
    return BREAKER_PRODUCT_LABELS[lowerValue];
  }

  const compactValue = lowerValue.split("(")[0].trim();
  return BREAKER_PRODUCT_LABELS[compactValue] || normalized;
};

const parseDisplayDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
  const match = raw.match(ddmmyyyy);

  if (match) {
    const [, day, month, year] = match;
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDisplayDate = (value) => {
  if (!value) return "-";

  const parsed = parseDisplayDate(value);
  if (!parsed) return value;

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).replace(/\//g, "-");
};

export const calculateDueDate = (value) => {
  const servicedDate = parseDisplayDate(value);
  if (!servicedDate) return "-";

  const dueDate = new Date(servicedDate);
  dueDate.setFullYear(dueDate.getFullYear() + 1);
  dueDate.setDate(dueDate.getDate() - 1);

  return formatDisplayDate(dueDate);
};

export const getSerialQrUrl = (serialNo) => {
  const normalized = String(serialNo || "").trim();
  if (!normalized || typeof window === "undefined") return "";

  const currentOrigin = window.location.origin.replace(/\/+$/, "");
  let frontendBaseUrl = currentOrigin;

  try {
    const { hostname } = new URL(currentOrigin);
    if (hostname === "genmech.in" || (hostname.endsWith(".genmech.in") && hostname !== "portal.genmech.in")) {
      frontendBaseUrl = "https://portal.genmech.in";
    }
  } catch {
    frontendBaseUrl = currentOrigin;
  }

  const targetUrl = `${frontendBaseUrl}/serial/${encodeURIComponent(normalized)}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(targetUrl)}`;
};

const parseItemAndQuantity = (val) => {
  const str = String(val || "").trim();
  let qty = 1;
  let name = str;

  // Pattern 1: number at the start, optional 'x' or 'pcs' or space, e.g. "2x Front cover" or "2 Front cover"
  const startMatch = str.match(/^(\d+)\s*(?:x|pcs|pc|unit|units)?\s+(.+)$/i);
  if (startMatch) {
    qty = parseInt(startMatch[1], 10);
    name = startMatch[2];
  } else {
    // Pattern 2: number in parentheses or after dash at the end, e.g. "Front cover (2)" or "Front cover - 2"
    const endMatch = str.match(/^(.+?)\s*(?:-|\(|x)?\s*(\d+)\s*(?:pcs|pc|unit|units|\))?$/i);
    if (endMatch) {
      qty = parseInt(endMatch[2], 10);
      name = endMatch[1];
    }
  }

  // Clean name from any trailing/leading symbols like brackets, dashes, etc.
  name = name.replace(/^[\s():-]+|[\s():-]+$/g, "").trim();

  return { name, qty };
};

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-800 break-words">{value || "-"}</div>
    </div>
  );
}

export default function SerialProductDetails({
  product,
  visit,
  complaint,
  showQr = false,
  showVisitDetails = true
}) {
  const p = product || {};
  const visitDetails = visit || {};
  const complaintDetails = complaint || {};
  const isBreaker = p.category === "breaker";
  const isAbb = p.category === "abb_warranty" || p.category === "abb" || p.category === "abbwarranty" || Boolean(p.natureOfComplaint) || Boolean(p.abbTicketNo);
  const productDisplayName = isBreaker
    ? getBreakerDisplayName(p.productName || p.product)
    : isAbb
    ? (p.product || p.productName || (p.productMakeModel !== "ABB" ? p.productMakeModel : "") || p.model || "ABB Warranty")
    : p.product || p.productName || "";
  const serviceDate = visitDetails.visitDate || visitDetails.date || p.visitDate || "";
  const dueDate = calculateDueDate(serviceDate);
  const qrUrl = (showQr && !isAbb) ? getSerialQrUrl(p.serialNo) : "";

  // Helper to check if a value is filled and not "-"
  const isValidValue = (val) => {
    if (val === null || val === undefined) return false;
    if (React.isValidElement(val)) return true;
    const str = String(val).trim();
    return str !== "" && str !== "-";
  };

  const overviewFields = [
    ...(showVisitDetails ? [{ label: "Category", value: isAbb ? "ABB WARRANTY" : (p.category ? String(p.category).toUpperCase() : "") }] : []),
    ...(isAbb
        ? [
          { label: "Nature of Complaint", value: p.natureOfComplaint || p.complaint || complaintDetails.issue },
          { label: "ABB Ticket No", value: p.abbTicketNo || p.ticketNo },
          { label: "Product", value: p.product || p.productName || (p.productMakeModel !== "ABB" ? p.productMakeModel : "") },
          { label: "Model", value: p.model },
          { label: "Panel Builder", value: p.panelBuilder },
          { label: "Serial No", value: p.serialNo },
          { label: "Number of Poles", value: p.pole },
          { label: "Frame Size / Rating", value: p.frameRating || p.rating },
          { label: "Commissioning Date", value: formatDisplayDate(p.commissioningDate) },
          { label: "Visit Date", value: formatDisplayDate(p.visitDate || serviceDate) },
          { label: "Operations per Day", value: p.operationsPerDay },
          { label: "Type of Release", value: p.releaseType },
          { label: "Type of Connection", value: p.connectionType },
          { label: "Cable / Busbar Size & Runs", value: p.cableBusbarSize && p.numberOfRuns ? `${p.cableBusbarSize} (${p.numberOfRuns})` : (p.cableBusbarSize || p.numberOfRuns) },
          { label: "Busbar Support", value: p.busbarSupport },
          { label: "Damage to Operating Mechanism", value: p.mechanismDamage === "Yes" ? `Yes – ${p.mechanismDamageDetails || ""}` : p.mechanismDamage },
          { label: "Loose Springs / Screws", value: p.looseSprings === "Yes" ? `Yes – ${p.looseSpringsDetails || ""}` : p.looseSprings },
          { label: "Other Visible Damage", value: p.otherVisibleDamage === "Yes" ? `Yes – ${p.otherVisibleDamageDetails || ""}` : p.otherVisibleDamage },
          { label: "Burn Marks near Air Vents", value: p.burnMarksAirVents === "Yes" ? `Yes – ${p.burnMarksAirVentsDetails || ""}` : p.burnMarksAirVents },
          { label: "Load Current", value: p.loadCurrent ? `${p.loadCurrent} A` : "" },
          { label: "Type of Load", value: p.loadType === "Other" ? p.loadTypeOther : p.loadType },
          { label: "Remarks", value: p.remarks },
        ]
      : isBreaker
        ? [
          { label: "Make", value: p.make || p.makeOther },
          { label: "Feeder", value: p.feeder },
          { label: "Panel Area", value: p.panelArea },
          { label: "Rating", value: p.rating },
          { label: "No of Poles", value: p.pole },
          { label: "Neutral", value: p.neutral },
          { label: "Breaker Type", value: p.breakerType },
          { label: "Sub Type", value: p.subType },
          { label: "Release S.No Number", value: p.releaseSerialNumber },
          { label: "Frame", value: p.frame },
        ]
      : [
          { label: "Make", value: p.make || p.makeOther },
          { label: "Rating", value: p.rating },
          { label: "No of Poles", value: p.pole },
          { label: "Model", value: p.model },
          { label: "Voltage", value: p.voltage },
          { label: "Speed (RPM)", value: p.speed }
        ]),
    { label: "Service Date", value: formatDisplayDate(serviceDate) },
    { label: "Due Date", value: dueDate },
    { label: "Warranty", value: p.warranty },
    { label: "Test Certificate No", value: p.certificateNumber || p.certificate_number || p.testCertificateNumber },
    { label: isAbb ? "Product Status" : "Service Status", value: (isBreaker ? p.serviceStatus : (p.serviceStatus || p.status)) }
  ].filter((field) => isValidValue(field.value));

  const visitFields = [
    { label: "Report ID", value: visitDetails.reportId || p.reportId },
    { label: "Visit ID", value: visitDetails.visitId || p.visitId },
    { label: "Customer", value: visitDetails.customerName },
    { label: "Company", value: visitDetails.company },
    { label: "Engineer", value: visitDetails.engineer },
    { label: "Final Status", value: visitDetails.finalStatus },
    { label: "Approval Status", value: visitDetails.approvalStatus },
    { label: "Complaint ID", value: complaintDetails.complaintId || visitDetails.complaintId },
    { label: "Complaint Status", value: complaintDetails.status },
    { label: "Contact Name", value: visitDetails.contactName },
    { label: "Phone", value: visitDetails.phone ? <PhoneActions phone={visitDetails.phone} /> : "" },
    { label: "Email", value: visitDetails.email },
    { label: "City", value: visitDetails.city }
  ].filter((field) => isValidValue(field.value));

  const detailTextFields = [
    ...(isBreaker ? [{ label: "Product Remarks", value: p.remarks }] : []),
    ...(isAbb ? [{ label: "Observation", value: p.observation || visitDetails.observation }] : !isBreaker ? [{ label: "Product Observation", value: p.observation }] : []),
    ...(isAbb ? [{ label: "Action Taken", value: p.actionTaken || visitDetails.actionTaken }] : !isBreaker ? [{ label: "Product Action Taken", value: p.actionTaken }] : [])
  ].filter((field) => field.value);

  const customBoxes = Array.isArray(p.additionalBoxes)
    ? p.additionalBoxes.filter((b) => b && (b.heading || b.content || b.value))
    : [];

  const additionalInfoFields = [
    ...(p.additionalInfo ? [{ label: "More Information", value: p.additionalInfo }] : []),
    ...customBoxes.map((b) => ({ label: b.heading || b.title || "Additional Details", value: b.content || b.value || "" })),
    ...(showVisitDetails ? [{ label: "Complaint Details", value: complaintDetails.issue || visitDetails.complaint }] : []),
    ...(showVisitDetails && !isAbb ? [{ label: "Visit Observation", value: visitDetails.observation }] : []),
    ...(showVisitDetails && !isAbb ? [{ label: "Visit Action Taken", value: visitDetails.actionTaken }] : [])
  ].filter((field) => field.value);

  const compReplaceList = React.useMemo(() => {
    if (Array.isArray(p.componentsToBeReplaced) && p.componentsToBeReplaced.length > 0) {
      return p.componentsToBeReplaced.map((c) => ({
        rawText: c.name || "",
        date: c.identifiedDate || visitDetails.visitDate || visitDetails.date || ""
      })).filter((item) => item.rawText);
    }
    const oldComps = Array.isArray(p.compReplace) ? p.compReplace.filter(Boolean) : [];
    return oldComps.map((str) => ({
      rawText: String(str || "").trim(),
      date: visitDetails.visitDate || visitDetails.date || ""
    }));
  }, [p.componentsToBeReplaced, p.compReplace, visitDetails.visitDate, visitDetails.date]);

  const newCompList = React.useMemo(() => {
    if (Array.isArray(p.componentsReplaced) && p.componentsReplaced.length > 0) {
      return p.componentsReplaced.map((c) => ({
        rawText: c.name ? `${c.quantity || 1} ${c.name}` : "",
        date: c.replacementDate || visitDetails.visitDate || visitDetails.date || ""
      })).filter((item) => item.rawText);
    }
    const oldComps = Array.isArray(p.newComp) ? p.newComp.filter(Boolean) : [];
    return oldComps.map((str) => ({
      rawText: String(str || "").trim(),
      date: visitDetails.visitDate || visitDetails.date || ""
    }));
  }, [p.componentsReplaced, p.newComp, visitDetails.visitDate, visitDetails.date]);

  const accessories = [
    { label: "Spring Charging Motor", voltages: p.motorVoltages, status: p.motorStatus, currentType: p.motorCurrentType },
    { label: "YO Opening Coil", voltages: p.yoVoltages, status: p.yoStatus, currentType: p.yoCurrentType },
    { label: "YC Closing Coil", voltages: p.ycVoltages, status: p.ycStatus, currentType: p.ycCurrentType },
    { label: "UV Under Voltage Coil", voltages: p.uvVoltages, status: p.uvStatus, currentType: p.uvCurrentType }
  ];
  const hasAccessories = accessories.some((item) => (item.voltages && item.voltages.length > 0) || item.status);

  // LSIG Visibility Config mapping exactly to server PDF rules:
  const hasExplicitTablePreference = Object.prototype.hasOwnProperty.call(p, "showLsigTable");
  const hasExplicitMeasuredPreference = Object.prototype.hasOwnProperty.call(p, "showLsigMeasuredValues");
  const includeMeasuredValues = hasExplicitMeasuredPreference ? Boolean(p.showLsigMeasuredValues) : true;
  const showLsigTable = includeMeasuredValues || (hasExplicitTablePreference ? Boolean(p.showLsigTable) : true);

  const lsig = Array.isArray(p.lsig) ? p.lsig : [];
  const hasLSIG = lsig.some((row) => row.setting || row.seconds || row.current || row.measured || row.status);

  return (
    <div className="space-y-5">
      <div className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,_rgba(248,250,252,1),_rgba(255,255,255,1),_rgba(239,246,255,0.78))] p-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Product</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{productDisplayName || "-"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Serial No</p>
              <p className="mt-1 text-lg font-semibold text-slate-800">{p.serialNo || "-"}</p>
            </div>
          </div>

          {showQr && !isAbb && qrUrl && (
            <div className="flex flex-col items-center gap-3">
              <img src={qrUrl} alt={`QR for ${p.serialNo || "serial number"}`} className="h-40 w-40 rounded-2xl border border-slate-200 bg-white p-3" />
              <p className="text-xs font-medium text-slate-500">Scan QR for serial details</p>
            </div>
          )}
        </div>
      </div>

      {overviewFields.length > 0 && (
        <div>
          <p className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-600">Product Details</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {overviewFields.map((field) => (
              <InfoCard key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        </div>
      )}

      {detailTextFields.length > 0 && (
        <div className="space-y-3">
          {detailTextFields.map((field) => (
            <div key={field.label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{field.label}</p>
              <p className="mt-1 text-sm leading-6 text-slate-700 whitespace-pre-wrap break-words">{field.value}</p>
            </div>
          ))}
        </div>
      )}

      {showVisitDetails && visitFields.length > 0 && (
        <>
          <div>
            <p className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-600">Visit Details</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {visitFields.map((field) => (
                <InfoCard key={field.label} label={field.label} value={field.value} />
              ))}
            </div>
          </div>

          {visitDetails.address && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Address</p>
              <p className="mt-1 text-sm leading-6 text-slate-700">{visitDetails.address}</p>
            </div>
          )}
        </>
      )}

      {(isBreaker || isAbb) && hasAccessories && (
        <div>
          <p className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-600">Motors &amp; Coils</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {accessories.map((acc) => {
              const voltages = Array.isArray(acc.voltages) ? acc.voltages : [];
              if (voltages.length === 0 && !acc.status) return null;

              return (
                <div key={acc.label} className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-indigo-400">{acc.label}</p>
                  {acc.currentType && (
                    <p className="mt-2 text-xs font-semibold text-indigo-700">{acc.currentType}</p>
                  )}
                  {voltages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {voltages.map((voltage) => {
                        const vStr = String(voltage || "").trim();
                        const displayVoltage = /^\d+$/.test(vStr) ? `${vStr}V` : vStr;
                        return (
                          <span key={voltage} className="rounded-full bg-indigo-200 px-2.5 py-0.5 text-xs font-semibold text-indigo-800">
                            {displayVoltage}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {acc.status && (
                    <span
                      className={`mt-2 inline-block rounded-full px-3 py-0.5 text-xs font-bold ${
                        acc.status === "OK" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {acc.status}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isBreaker && showLsigTable && hasLSIG && (
        <div>
          <p className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-600">LSIG Settings</p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Function</th>
                  <th className="px-3 py-2 font-semibold">Setting</th>
                  <th className="px-3 py-2 font-semibold">Time (s)</th>
                  <th className="px-3 py-2 font-semibold">Current (A)</th>
                  {includeMeasuredValues && <th className="px-3 py-2 font-semibold">Curve</th>}
                  {includeMeasuredValues && <th className="px-3 py-2 font-semibold">Fault Current (A)</th>}
                  {includeMeasuredValues && <th className="px-3 py-2 font-semibold">Measured</th>}
                  {includeMeasuredValues && <th className="px-3 py-2 font-semibold">Status</th>}
                </tr>
              </thead>
              <tbody>
                {lsig.map((row, index) => (
                  <tr key={`${row.type || "lsig"}-${index}`} className="border-t border-slate-200 text-center hover:bg-slate-50">
                    <td className="px-3 py-2 font-bold text-slate-800">{row.type || "-"}</td>
                    <td className="px-3 py-2 text-slate-600">{row.func || "-"}</td>
                    <td className="px-3 py-2 text-slate-700">{row.setting || "-"}</td>
                    <td className="px-3 py-2 text-slate-700">{row.type === "I" ? "N/A" : (row.seconds || "-")}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{row.current || "-"}</td>
                    {includeMeasuredValues && (
                      <td className="px-3 py-2 font-medium text-slate-600">
                        {row.formula || (row.type === "L" ? "t = k/i²" : row.type === "I" ? "Immediate" : "I = set × In")}
                      </td>
                    )}
                    {includeMeasuredValues && (
                      <td className="px-3 py-2 font-bold text-slate-800">
                        {row.tripCurrent || "-"}
                      </td>
                    )}
                    {includeMeasuredValues && <td className="px-3 py-2 text-slate-700">{row.measured || "-"}</td>}
                    {includeMeasuredValues && (
                      <td className="px-3 py-2">
                        {row.status ? (
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                              row.status === "OK" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                            }`}
                          >
                            {row.status}
                          </span>
                        ) : "-"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isAbb && (
        <div>
          <p className="mb-3 text-sm font-bold uppercase tracking-wider text-teal-800 flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white">✓</span>
            Continuity Test (OK / NOT OK)
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "R – R", val: p.resistanceRR || p.resistanceRY },
              { label: "Y – Y", val: p.resistanceYY || p.resistanceYB },
              { label: "B – B", val: p.resistanceBB || p.resistanceBR },
              { label: "N – N", val: p.resistanceNN || p.resistanceRN || p.resistanceBN }
            ].map((m) => {
              const normalized = String(m.val || "").trim().toUpperCase();
              const isOk = normalized === "OK" || normalized === "OKAY";
              const isNotOk = normalized === "NOT OK" || normalized === "NOT OKAY" || normalized === "NO OK";
              return (
                <div key={m.label} className="rounded-xl border border-teal-200 bg-white p-3 text-center shadow-xs">
                  <span className="block text-xs font-bold text-slate-700 mb-1.5">{m.label}</span>
                  {isOk ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                      ✓ OK
                    </span>
                  ) : isNotOk ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-800">
                      ✗ NOT OK
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      {m.val || "-"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(compReplaceList.length > 0 || newCompList.length > 0) && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {compReplaceList.length > 0 && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2">Components to be Replaced</p>
              <div className="space-y-1.5">
                {compReplaceList.map((c, index) => {
                  const { name, qty } = parseItemAndQuantity(c.rawText);
                  return (
                    <div key={`${c.rawText}-${index}`} className="flex flex-col gap-1 text-sm text-red-700 bg-white/70 rounded-xl px-3 py-2 border border-red-100 shadow-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{name}</span>
                        <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                          Qty: {qty}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 font-medium mt-0.5">
                        <span className="material-symbols-outlined text-[12px]">calendar_today</span>
                        <span>Date: {formatDisplayDate(c.date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {newCompList.length > 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-500 mb-2">Components Replaced</p>
              <div className="space-y-1.5">
                {newCompList.map((c, index) => {
                  const { name, qty } = parseItemAndQuantity(c.rawText);
                  return (
                    <div key={`${c.rawText}-${index}`} className="flex flex-col gap-1 text-sm text-emerald-700 bg-white/70 rounded-xl px-3 py-2 border border-emerald-100 shadow-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{name}</span>
                        <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                          Qty: {qty}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 font-medium mt-0.5">
                        <span className="material-symbols-outlined text-[12px]">calendar_today</span>
                        <span>Date: {formatDisplayDate(c.date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {additionalInfoFields.length > 0 && (
        <div className="space-y-3">
          {additionalInfoFields.map((field) => (
            <div key={field.label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{field.label}</p>
              <p className="mt-1 text-sm leading-6 text-slate-700 whitespace-pre-wrap break-words">{field.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
